<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <Button
            v-if="getUserPermission('CREATE_LIEN')"
            :label="$t('Add')"
            size="md"
            @click="goToAddLien"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="lienRef"
      :busy="isBusy"
      :items="lienList"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(formOfLien)="data">

        {{ addSpaceToString(data.value) }}

      </template>
      <!-- Column: Commencement Date -->

      <template #cell(commencementDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <!-- Column: Maturity Date -->
      <template #cell(maturityDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

      <!-- Column: Maturity Date -->
      <template #cell(dateCreated)="data">

        {{ toDateWithYear(data.value) }}

      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission('MODIFY_LIEN')"
              @click="onSelectLienItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">
                {{ $t('Modify') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectLienItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">
                {{ $t('View') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_LIEN' : 'ACTIVATE_LIEN')"
              @click="lienStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Disabled') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :busy="isBusy"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="lienRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="lienFormData.accountNumber"
                :label="$t('Account Number')"
                type="text"
                :error-name="$t('Account Number')"
                for="account-number"
                readonly
              />
            </b-col>
            <!-- Account Number -->

            <!--  Form of Lien -->
            <b-col md="6">
              <SelectField
                id="form-of-lien"
                v-model.trim="lienFormData.formOfLien"
                :label="$t('Form of Lien')"
                :options="formOfLienOptions"
                :placeholder="$t('Select')"
                :error-name="$t('Form of Lien')"
                rules="required"
                item="label"
                :reduce="option => option.value"
                :set-selected="setFormOfLien"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Form of Lien -->
          </b-row>

          <b-row class="match-height">
            <!--Amount-->
            <b-col md="6">
              <InputNumberField
                id="amount"
                v-model.number="lienFormData.amount"
                :label="$t('Amount')"
                :error-name="$t('Amount')"
                :rules="isFlatAmountRequired"
                :readonly="isFlatAmount"
              />
            </b-col>
            <!-- Amount -->

            <!-- Percentage -->
            <b-col md="6">
              <InputField
                id="percentage"
                v-model.number="lienFormData.percentage"
                :label="$t('Percentage')"
                type="number"
                :rules="isPercentageRequired"
                :error-name="$t('Percentage')"
                :readonly="isPercentage"
              />
            </b-col>
            <!-- Percentage -->
          </b-row>

          <b-row class="match-height">
            <!-- Description-->
            <b-col md="6">
              <InputField
                id="description"
                v-model="lienFormData.description"
                :label="$t('Description')"
                type="text"
                :error-name="$t('Description')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Description -->

            <!-- Tenure -->
            <b-col md="6">
              <InputField
                id="tenure"
                v-model.number="lienFormData.tenure"
                :label="$t('Tenure (In days)')"
                type="number"
                :error-name="$t('Tenure')"
                rules="required|integer"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Tenure -->
          </b-row>

          <b-row class="match-height">
            <!-- Commencement Date-->
            <b-col md="6">
              <DatePickerField
                id="commencement-date"
                v-model="lienFormData.commencementDate"
                rules="required"
                :label="$t('Commencement Date')"
                :config="datePickerConfig"
                :on-change="setMaturityDate"
                :disabled="isReadonly"
              />
            </b-col>
            <!--  Commencement Date -->

            <!-- Maturity Date -->
            <b-col md="6">
              <InputField
                id="maturity-date"
                v-model="maturityDate"
                :label="$t('Maturity Date')"
                type="text"
                for="maturity-date"
                readonly
              />
            </b-col>
            <!-- Maturity Date -->

          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchLiens"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="number"
            :formatter="checkAccountNumberLimit"
            for="account-number"
          />
        </b-col>
        <!-- account Number -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="lienStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge,
  BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required, maxValue, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import {
  formatAmount, toBalance, toAmount, toKobo,
} from '@/helpers/formatAmount'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { gender, accessLevel, lienStatus } from '../../../../../helpers/data'
import { toYearWithDate, toDateWithYear } from '../../../../../helpers/formatDate'
import getStatusColor from '../../../../../helpers/getStatusColor'
import getEndDate from '../../../../../helpers/getEndDate'
import addSpaceToString from '../../../../../helpers/addSpaceToString'
import Button from '../../../../components/button/Button.vue'
import { accountPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BDropdown,
    BDropdownItem,
    DatePickerField,
    BModal,
    BForm,
    BSpinner,
    SelectField,
    ValidationObserver,
    InputField,
    InputNumberField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      showSpinner: false,
      isReadonly: false,
      maxValue,
      toAmount,
      addSpaceToString,
      formatAmount,
      toBalance,
      positive,
      sortBy: '',
      formOfLienOptions: [
        { label: 'Flat amount', value: 'FlatAmount' },
        { label: 'Percentage of balance', value: 'PercentageOfBalance' },
      ],
      lienFormData: {
        accountNumber: '',
        formOfLien: null,
        amount: null,
        percentage: null,
        description: '',
        tenure: null,
        commencementDate: null,
      },
      getEndDate,
      required,
      modalTitle: '',
      getStatusColor,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'formOfLien', sortable: true, label: 'Form Of Lien' },
        { key: 'tenure', sortable: true, label: 'Tenure' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'percentage', sortable: true, label: 'Percentage' },
        { key: 'commencementDate', sortable: true, label: 'Commencement Date' },
        { key: 'maturityDate', sortable: true, label: 'Maturity Date' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      toDateWithYear,
      toYearWithDate,
      maturityDate: '',
      genderOptions: gender,
      buttonLabel: this.$t('Save'),
      isFlatAmountRequired: '',
      isPercentageRequired: '',
      isFlatAmount: false,
      isPercentage: false,
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      setMaturityDate(dateStr) {
        this.maturityDate = getEndDate(dateStr, this.lienFormData.tenure)
      },
      filter: {
        name: '',
        accountNumber: '',
        status: '',
        sortColumn: '',
        direction: '',
      },
      accessLevelOptions: accessLevel,
      lienList: [],
      branchOptions: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    lienStatusOptions() {
      return lienStatus.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    formOfLien(newValue) {
      this.lienFormData.formOfLien = newValue
    },
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLiens()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLiens()
    },
  },
  mounted() {
    this.fetchLiens()
  },

  methods: {
    ...mapActions({
      getAllLien: 'account/getLienList',
      updateLien: 'account/updateLienAction',
      updateLienStatus: 'account/updateStatus',
    }),
    goToAddLien() {
      this.$router.push('add-liens')
    },
    checkAccountNumberLimit(e) {
      return String(e).substring(0, 10)
    },
    setFormOfLien(selectedValue) {
      if (selectedValue === 'FlatAmount') {
        this.isFlatAmount = false
        this.isPercentage = true
        this.lienFormData.percentage = 0
        this.isFlatAmountRequired = 'required|positive'
        this.isPercentageRequired = ''
      } else {
        this.isPercentage = false
        this.isFlatAmount = true
        this.lienFormData.amount = 0
        this.isFlatAmountRequired = ''
        this.isPercentageRequired = 'required|positive|max-value:100'
      }
    },
    async lienStatus(items) {
      const { id, accountName } = items
      const actionType = items.status === 'Active' ? 'Disabled' : 'Active'
      const action = items.status === 'Active' ? this.$t('Disabled') : this.$t('Activate')
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_LIEN : accountPermissions.ACTIVATE_LIEN

      const text1 = this.$t('Your request to')
      const text2 = this.$t('Account Lien was successful')
      const text3 = this.$t('Account Lien failed. Reason')
      const text4 = this.$t('Are you sure you want to')
      const text5 = this.$t('this Account Lien?')
      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name: accountName,
          }
          const response = await this.updateLienStatus({
            statusValues, actionType, routeName: 'AccountLien', permission,
          })
          if (response.status === 200) {
            this.fetchLiens()
            this.alertSuccess({ text: `${text1} ${action} ${text2}` })
            this.showSpinner = false
          }
        } catch (error) {
          this.alertError({ text: `${text1} ${action} ${text3}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(updateStatus, { title: '', text: `${text4} ${action} ${text5}` })
    },
    async fetchLiens() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getAllLien({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.lienList = data.data
          this.totalCount = data.recordCount
        } else {
          this.lienList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    onSelectLienItem(items, activity, val) {
      this.lienFormData = {
        ...items,
        amount: items.amount === 0 ? items.amount.toString() : formatAmount(items.amount),
        percentage: items.percentage === 0 ? items.percentage.toString() : items.percentage,
        commencementDate: this.toDateWithYear(items.commencementDate),
      }
      this.maturityDate = this.toDateWithYear(items.maturityDate)
      this.datePickerConfig.defaultDate = this.toDateWithYear(items.commencementDate)

      this.isPercentage = (val === 'edit' && items.formOfLien === 'PercentageOfBalance')
        ? this.isPercentage = false : this.isPercentage = true
      this.isFlatAmount = (val === 'edit' && items.formOfLien === 'FlatAmount')
        ? this.isFlatAmount = false : this.isFlatAmount = true
      this.isReadonly = val !== 'edit'
      this.modalTitle = val === 'edit' ? this.$t('Edit Lien') : this.$t('Lien')
      this.buttonLabel = this.$t('Update')
      // this.datePickerConfig.allowInput = val !== 'edit'
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.lienRules.validate().then(async success => {
        if (success) {
          const text1 = this.$t('Are you sure you want to')
          const text2 = this.$t('this Account Lien?')
          this.confirm(this.updateLienAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      })
    },
    async updateLienAction() {
      const text1 = this.$t('Your request to')
      const text2 = this.$t('Account Lien was successful')
      const text3 = this.$t('Account Lien failed. Reason')
      try {
        this.isLoading = true
        const response = await this.updateLien({
          ...this.lienFormData,
          amount: toKobo(this.lienFormData.amount),
          commencementDate: this.toYearWithDate(this.lienFormData.commencementDate),
        })
        const { status } = await response
        if (status === 200) {
          this.fetchLiens()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${text1} ${this.buttonLabel} ${text3}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isBusy = false
      }
    },

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchLiens()
    },
    resetModal() {
      this.isPercentage = false
      this.isLoading = false
      this.isFlatAmount = false
      this.isReadonly = false
      this.modalTitle = this.$t('Lien')
      this.buttonLabel = this.$t('Update')
    },
  },
}
</script>
